import { Button } from "../ui/button";
import { ArrowUp, ArrowDown, Edit2, Trash2, User } from "lucide-react";
import type { TestimonialItem } from "./useTestimonialsManager";

interface TestimonialRowActionsProps {
  testimonial: TestimonialItem;
  idx: number;
  totalCount: number;
  onEdit: (t: TestimonialItem) => void;
  onDelete: (id: string) => void;
  onMove: (index: number, direction: "up" | "down") => void;
}

function TestimonialRowActions({
  testimonial,
  idx,
  totalCount,
  onEdit,
  onDelete,
  onMove,
}: TestimonialRowActionsProps) {
  return (
    <>
      <td className="p-4 text-center">
        <div className="flex items-center justify-center gap-1">
          <Button
            size="icon"
            variant="ghost"
            onClick={() => onMove(idx, "up")}
            disabled={idx === 0}
            className="h-7 w-7 text-muted-foreground hover:text-foreground"
          >
            <ArrowUp className="h-4.5 w-4.5" />
          </Button>
          <Button
            size="icon"
            variant="ghost"
            onClick={() => onMove(idx, "down")}
            disabled={idx === totalCount - 1}
            className="h-7 w-7 text-muted-foreground hover:text-foreground"
          >
            <ArrowDown className="h-4.5 w-4.5" />
          </Button>
        </div>
      </td>
      <td className="p-4 text-center">
        <div className="flex items-center justify-center gap-2">
          <Button
            size="icon"
            variant="outline"
            onClick={() => onEdit(testimonial)}
            className="h-8 w-8 border-border/60 hover:bg-brand-primary/10 hover:text-brand-primary text-muted-foreground"
          >
            <Edit2 className="h-4 w-4" />
          </Button>
          <Button
            size="icon"
            variant="outline"
            onClick={() => onDelete(testimonial.id)}
            className="h-8 w-8 border-border/60 hover:bg-destructive/10 hover:text-destructive text-muted-foreground"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </td>
    </>
  );
}

interface TestimonialRowProps {
  testimonial: TestimonialItem;
  idx: number;
  totalCount: number;
  onEdit: (t: TestimonialItem) => void;
  onDelete: (id: string) => void;
  onMove: (index: number, direction: "up" | "down") => void;
}

function TestimonialRow({
  testimonial,
  idx,
  totalCount,
  onEdit,
  onDelete,
  onMove,
}: TestimonialRowProps) {
  return (
    <tr className="hover:bg-muted/10 transition-colors">
      <td className="p-4 text-center font-mono font-bold text-muted-foreground">
        {idx + 1}
      </td>
      <td className="p-4 w-16">
        {testimonial.avatar_url ? (
          <img
            src={testimonial.avatar_url}
            alt={testimonial.author_en}
            className="w-10 h-10 rounded-full object-cover border border-border/80"
          />
        ) : (
          <div className="w-10 h-10 rounded-full bg-muted/30 border border-border/60 flex items-center justify-center text-muted-foreground">
            <User className="h-5 w-5" />
          </div>
        )}
      </td>
      <td className="p-4 max-w-xs space-y-1">
        <p className="font-semibold text-foreground leading-snug">
          {testimonial.author_en}
        </p>
        {testimonial.author_tr && testimonial.author_tr !== testimonial.author_en && (
          <p className="text-xs text-muted-foreground italic">
            {testimonial.author_tr}
          </p>
        )}
      </td>
      <td className="p-4 max-w-xs space-y-1">
        <p className="text-xs font-semibold text-brand-secondary">
          {testimonial.role_en} @ {testimonial.company_en}
        </p>
        {(testimonial.role_tr || testimonial.company_tr) && (
          <p className="text-[11px] text-muted-foreground italic">
            {testimonial.role_tr || ""} @ {testimonial.company_tr || ""}
          </p>
        )}
      </td>
      <td className="p-4 max-w-md space-y-1">
        <p className="text-xs text-foreground/85 line-clamp-2 leading-relaxed">
          "{testimonial.quote_en}"
        </p>
        {testimonial.quote_tr && (
          <p className="text-[11px] text-muted-foreground/80 italic line-clamp-2 leading-relaxed">
            "{testimonial.quote_tr}"
          </p>
        )}
      </td>
      <TestimonialRowActions
        testimonial={testimonial}
        idx={idx}
        totalCount={totalCount}
        onEdit={onEdit}
        onDelete={onDelete}
        onMove={onMove}
      />
    </tr>
  );
}

interface TestimonialsListProps {
  testimonials: TestimonialItem[];
  onEdit: (t: TestimonialItem) => void;
  onDelete: (id: string) => void;
  onMove: (index: number, direction: "up" | "down") => void;
}

export function TestimonialsList({
  testimonials,
  onEdit,
  onDelete,
  onMove,
}: TestimonialsListProps) {
  const sorted = [...testimonials].sort((a, b) => a.sort_order - b.sort_order);

  if (sorted.length === 0) {
    return (
      <div className="text-center py-12 border border-dashed border-border/60 rounded-2xl bg-card/40">
        <p className="text-sm font-semibold text-muted-foreground">
          No testimonials configured. Click the button above to add one.
        </p>
      </div>
    );
  }

  return (
    <div className="border border-border/60 bg-card rounded-2xl overflow-hidden shadow-sm">
      <div className="overflow-x-auto">
        <table className="w-full text-left border-collapse font-sans">
          <thead>
            <tr className="bg-muted/40 border-b border-border/60 text-xs font-bold uppercase tracking-wider text-muted-foreground">
              <th className="p-4 w-12 text-center">#</th>
              <th className="p-4 w-16">Avatar</th>
              <th className="p-4">Author (EN / TR)</th>
              <th className="p-4">Credentials (EN / TR)</th>
              <th className="p-4">Quote Summary (EN / TR)</th>
              <th className="p-4 w-28 text-center">Order</th>
              <th className="p-4 w-36 text-center">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border/40 text-sm">
            {sorted.map((testimonial, idx) => (
              <TestimonialRow
                key={testimonial.id}
                testimonial={testimonial}
                idx={idx}
                totalCount={sorted.length}
                onEdit={onEdit}
                onDelete={onDelete}
                onMove={onMove}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default TestimonialsList;
